import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import {
  createRootRoute, createRoute, createRouter, Link, redirect, RouterProvider,
} from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/tokens.css";
import "./styles/base.css";
import { ApiRequestError, auth } from "./lib/api.ts";
import { wipeDevice } from "./lib/device.ts";
import { queryClient } from "./lib/queryClient.ts";
import { useT } from "./lib/i18n.ts";
import { OAuthConsent } from "./screens/OAuthConsent.tsx";
import { ensureGated } from "./lib/gate.ts";
import { Shell } from "./screens/Shell.tsx";
import { SignIn } from "./screens/SignIn.tsx";
import { Rest } from "./screens/Rest.tsx";
import { Deal } from "./screens/Deal.tsx";
import { PickOwn } from "./screens/PickOwn.tsx";
import { SetView } from "./screens/SetView.tsx";
import { Shelf } from "./screens/Shelf.tsx";
import { ItemDetail } from "./screens/ItemDetail.tsx";
import { AddItem } from "./screens/AddItem.tsx";
import { SettingsScreen } from "./screens/Settings.tsx";
import { AccountSettings, AppearanceSettings, ShelfSettings } from "./screens/SettingsPages.tsx";
import { Needs } from "./screens/Needs.tsx";
import { ConnectedServices } from "./screens/ConnectedServices.tsx";
import { Categories } from "./screens/Categories.tsx";
import { Moods } from "./screens/Moods.tsx";
import { Sources } from "./screens/Sources.tsx";
import { Connect } from "./screens/Connect.tsx";
import { Places } from "./screens/Places.tsx";
import { PlaceEdit } from "./screens/PlaceEdit.tsx";
import { Privacy, Terms } from "./screens/Legal.tsx";

const persister = createSyncStoragePersister({ storage: window.localStorage, key: "instead.cache" });

// D-18: browser errors → Sentry (EU org). Loaded lazily and only when a DSN
// is configured, so the default bundle carries no telemetry at all.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  void import("@sentry/react").then((S) =>
    S.init({ dsn: sentryDsn, environment: import.meta.env.MODE, tracesSampleRate: 0, sendDefaultPii: false })
  );
}

/** A path the app doesn't know (a stray link, a typo): say so, offer home. */
function NotFound() {
  const t = useT();
  return (
    <div className="empty" style={{ minHeight: "60vh", justifyContent: "center" }}>
      <p>{t("notFound.title")}</p>
      <Link className="btn btn-primary" to="/rest">{t("notFound.home")}</Link>
    </div>
  );
}

const rootRoute = createRootRoute({ component: Shell, notFoundComponent: NotFound });

const signinRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signin", component: SignIn });
const consentRoute = createRoute({ getParentRoute: () => rootRoute, path: "/oauth/consent", component: OAuthConsent });
// public, like /signin: the notice and the terms are readable before any account exists (D-38)
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: Privacy });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/terms", component: Terms });

const requireAuth = async () => {
  if (!auth.token()) throw redirect({ to: "/signin" });
  try {
    await ensureGated();
  } catch (e) {
    if (e instanceof ApiRequestError && e.code === "BETA_INVITE_REQUIRED") {
      throw redirect({ to: "/signin", search: { beta: "required" } as never });
    }
    if (e instanceof ApiRequestError && e.status === 401) {
      if (e.code === "ACCOUNT_DELETED") {
        // D-33: the identity was deleted; leave nothing behind and say why on the sign-in screen
        await wipeDevice();
        throw redirect({ to: "/signin", search: { deleted: "1" } as never });
      }
      auth.clear();
      throw redirect({ to: "/signin" });
    }
    // anything else (5xx): fail open to the app shell — server-side checks still hold
  }
};

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    if (!auth.token()) throw redirect({ to: "/signin" });
    throw redirect({ to: "/rest" });
  },
});
const parent = { getParentRoute: () => rootRoute, beforeLoad: requireAuth } as const;
const restRoute = createRoute({ ...parent, path: "/rest", component: Rest });
const dealRoute = createRoute({ ...parent, path: "/deal", component: Deal });
const pickRoute = createRoute({ ...parent, path: "/pick", component: PickOwn });
const setRoute = createRoute({ ...parent, path: "/set/$id", component: SetView });
const shelfRoute = createRoute({ ...parent, path: "/shelf", component: Shelf });
const itemRoute = createRoute({ ...parent, path: "/item/$id", component: ItemDetail });
const addRoute = createRoute({ ...parent, path: "/add", component: AddItem });
const settingsRoute = createRoute({ ...parent, path: "/settings", component: SettingsScreen });
const appearanceRoute = createRoute({ ...parent, path: "/settings/appearance", component: AppearanceSettings });
const shelfSettingsRoute = createRoute({ ...parent, path: "/settings/shelf", component: ShelfSettings });
// D-34: Rest settings live on the Shelf page now; bookmarks of the old page keep working.
const restSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/rest",
  beforeLoad: () => { throw redirect({ to: "/settings/shelf", replace: true }); },
});
const servicesRoute = createRoute({ ...parent, path: "/settings/services", component: ConnectedServices });
const accountRoute = createRoute({ ...parent, path: "/settings/account", component: AccountSettings });
const categoriesRoute = createRoute({ ...parent, path: "/settings/categories", component: Categories });
const moodsRoute = createRoute({ ...parent, path: "/settings/moods", component: Moods });
const needsRoute = createRoute({ ...parent, path: "/settings/needs", component: Needs });
const placesRoute = createRoute({ ...parent, path: "/settings/places", component: Places });
const placeEditRoute = createRoute({ ...parent, path: "/settings/places/$id", component: PlaceEdit });
const sourcesRoute = createRoute({ ...parent, path: "/sources", component: Sources });
const connectRoute = createRoute({ ...parent, path: "/connect", component: Connect });

const routeTree = rootRoute.addChildren([
  indexRoute, signinRoute, consentRoute, privacyRoute, termsRoute, restRoute, dealRoute, pickRoute, setRoute, shelfRoute, itemRoute, addRoute,
  settingsRoute, appearanceRoute, restSettingsRoute, shelfSettingsRoute, servicesRoute, accountRoute, categoriesRoute, moodsRoute, needsRoute, placesRoute, placeEditRoute, sourcesRoute, connectRoute,
]);

const router = createRouter({ routeTree });
declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 7 * 24 * 3600_000, buster: "v2" }}
    >
      <RouterProvider router={router} />
    </PersistQueryClientProvider>
  </StrictMode>
);
